/**
 * useContentImageCheck - 检查 Markdown 正文中插入的图片是否存在
 */
import { useState, useCallback } from 'react';
import { OutlineItem } from '../types';
import { getErrorMessage } from '../services/api';

export interface ImageInfo {
  line: number;
  alt: string;
  url: string;
  original: string; // 完整匹配 "![alt](url)"
  status: 'checking' | 'exists' | 'broken';
  errorMessage?: string;
}

export interface ChapterImageResult {
  item: OutlineItem;
  images: ImageInfo[];
}

interface UseContentImageCheckProps {
  effectiveLeafItems: OutlineItem[];
  projectId?: number;
  draftStore: { upsertChapterContent: (id: string, content: string) => void };
  generationMode: string;
  activeSection: 'tech' | 'biz';
  setBizLeafItems: React.Dispatch<React.SetStateAction<OutlineItem[]>>;
  setLeafItems: React.Dispatch<React.SetStateAction<OutlineItem[]>>;
  setMessage: React.Dispatch<React.SetStateAction<{ type: 'success' | 'error'; text: string } | null>>;
}

// 匹配 Markdown 图片语法 ![alt](url)
const IMAGE_REGEX = /!\[([^\]]*)\]\(([^)]+)\)/g;

// 检查单个图片 URL 是否可访问
function checkImageUrl(url: string, timeoutMs = 8000): Promise<boolean> {
  // Data URI 视为有效
  if (/^data:/.test(url)) {
    return Promise.resolve(true);
  }
  // http/https URL 通过加载检测
  if (/^https?:\/\//.test(url)) {
    return new Promise((resolve) => {
      const img = new Image();
      const timer = setTimeout(() => {
        img.src = '';
        resolve(false);
      }, timeoutMs);
      img.onload = () => {
        clearTimeout(timer);
        resolve(true);
      };
      img.onerror = () => {
        clearTimeout(timer);
        resolve(false);
      };
      img.src = url;
    });
  }
  // 相对路径或其他协议视为无效
  return Promise.resolve(false);
}

/**
 * 从内容中移除指定的 Markdown 图片
 * 会清理图片前后的空白行，保持格式整洁
 */
function removeImageFromContent(content: string, imageOriginal: string): string {
  // 转义特殊正则字符
  const escaped = imageOriginal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // 匹配图片及其前后的空白行
  const pattern = new RegExp(`\\n*${escaped}\\n*`, 'g');
  return content.replace(pattern, (match) => {
    // 如果前后都有换行，保留一个换行
    const hasLeadingNewline = match.startsWith('\n');
    const hasTrailingNewline = match.endsWith('\n');
    if (hasLeadingNewline && hasTrailingNewline) {
      return '\n';
    }
    return '';
  });
}

export function useContentImageCheck({
  effectiveLeafItems,
  projectId,
  draftStore,
  generationMode,
  activeSection,
  setBizLeafItems,
  setLeafItems,
  setMessage,
}: UseContentImageCheckProps) {
  const [imageResults, setImageResults] = useState<ChapterImageResult[] | null>(null);
  const [showImageCheck, setShowImageCheck] = useState(false);
  const [checkingImages, setCheckingImages] = useState(false);
  const [clearingIds, setClearingIds] = useState<Set<string>>(new Set());
  const [clearingAll, setClearingAll] = useState(false);

  /**
   * 检查所有章节中的图片
   */
  const checkAllImages = useCallback(async () => {
    setCheckingImages(true);
    const results: ChapterImageResult[] = [];

    try {
      // Step 1: 收集所有图片信息
      const allImageChecks: Promise<void>[] = [];

      for (const item of effectiveLeafItems) {
        if (!item.content) continue;

        const images: ImageInfo[] = [];
        let match: RegExpExecArray | null;
        const regex = new RegExp(IMAGE_REGEX.source, 'g');

        while ((match = regex.exec(item.content)) !== null) {
          const beforeMatch = item.content.substring(0, match.index);
          const line = beforeMatch.split('\n').length;
          images.push({
            line,
            alt: match[1] || '',
            url: match[2] || '',
            original: match[0],
            status: 'checking',
          });
        }

        if (images.length > 0) {
          results.push({ item, images });
        }
      }

      if (results.length === 0) {
        setImageResults([]);
        setShowImageCheck(true);
        setCheckingImages(false);
        return;
      }

      // Step 2: 并行检查所有图片 URL
      const checkPromises = results.flatMap((chapter) =>
        chapter.images.map(async (img) => {
          const exists = await checkImageUrl(img.url);
          img.status = exists ? 'exists' : 'broken';
          if (!exists && !img.errorMessage) {
            if (/^data:/.test(img.url)) {
              img.errorMessage = 'Base64 数据图片（已跳过检查）';
            } else if (/^https?:\/\//.test(img.url)) {
              img.errorMessage = '图片无法加载（不存在或网络不可达）';
            } else {
              img.errorMessage = '本地相对路径图片不存在';
            }
          }
        })
      );

      await Promise.all(checkPromises);

      // Step 3: 过滤只保留有问题的章节（有 broken 图片的）
      const chaptersWithBroken = results
        .map((ch) => ({
          ...ch,
          images: ch.images.filter((img) => img.status === 'broken'),
        }))
        .filter((ch) => ch.images.length > 0);

      setImageResults(chaptersWithBroken);
    } catch (err: any) {
      setMessage({ type: 'error', text: `图片检查失败: ${getErrorMessage(err, '未知错误')}` });
      setImageResults([]);
    } finally {
      setShowImageCheck(true);
      setCheckingImages(false);
    }
  }, [effectiveLeafItems, setMessage]);

  /**
   * 更新章节内容的通用方法
   */
  const updateChapterContent = useCallback(
    (itemId: string, newContent: string) => {
      const setItemsFunc =
        generationMode === 'full' && activeSection === 'biz' ? setBizLeafItems : setLeafItems;
      setItemsFunc((prev) =>
        prev.map((li) => (li.id === itemId ? { ...li, content: newContent } : li))
      );
      draftStore.upsertChapterContent(itemId, newContent);
      if (projectId) {
        const section = generationMode === 'full' ? activeSection : 'tech';
        import('../services/api').then(({ projectApi }) => {
          projectApi.saveChapterContent(projectId, itemId, newContent, section).catch(() => {});
        });
      }
    },
    [projectId, draftStore, generationMode, activeSection, setBizLeafItems, setLeafItems]
  );

  /**
   * 清除单个章节中的所有无效图片
   */
  const clearChapterImages = useCallback(
    async (chapter: ChapterImageResult) => {
      const item = chapter.item;
      if (!item.content) return;

      setClearingIds((prev) => new Set(prev).add(item.id));
      try {
        let newContent = item.content;
        for (const img of chapter.images) {
          newContent = removeImageFromContent(newContent, img.original);
        }
        updateChapterContent(item.id, newContent);

        // 从结果中移除该章节
        setImageResults((prev) => (prev || []).filter((r) => r.item.id !== item.id));
        setMessage({ type: 'success', text: `已清除 "${item.title}" 中的 ${chapter.images.length} 个无效图片` });
      } catch (err: any) {
        setMessage({ type: 'error', text: `清除失败: ${getErrorMessage(err, '请稍后重试')}` });
      } finally {
        setClearingIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }
    },
    [updateChapterContent, setMessage]
  );

  /**
   * 清除所有无效图片
   */
  const clearAllBrokenImages = useCallback(async () => {
    if (!imageResults || imageResults.length === 0) return;

    setClearingAll(true);
    let successCount = 0;
    try {
      for (const chapter of imageResults) {
        const item = chapter.item;
        if (!item.content) continue;

        let newContent = item.content;
        for (const img of chapter.images) {
          newContent = removeImageFromContent(newContent, img.original);
        }
        updateChapterContent(item.id, newContent);
        successCount++;
      }

      setImageResults([]);
      setMessage({
        type: 'success',
        text: `已清除 ${successCount} 个章节中的全部无效图片`,
      });
    } catch (err: any) {
      setMessage({ type: 'error', text: `清除失败: ${getErrorMessage(err, '请稍后重试')}` });
    } finally {
      setClearingAll(false);
    }
  }, [imageResults, updateChapterContent, setMessage]);

  return {
    // State
    imageResults,
    showImageCheck,
    setShowImageCheck,
    checkingImages,
    clearingIds,
    clearingAll,
    // Actions
    checkAllImages,
    clearChapterImages,
    clearAllBrokenImages,
  };
}
